import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { initAnalytics, updateConsent } from "../utils/analytics";

const STORAGE_KEY = "koning_consent_v1";

/**
 * AVG-compliant cookie/consent state.
 * Categories:
 *   - functional (always on, no opt-out)
 *   - analytics  (GA4)
 *   - marketing  (Meta Pixel + Google Ads)
 *
 * Stored as {analytics, marketing, decidedAt} in localStorage.
 */
const ConsentContext = createContext(null);

const DEFAULT_STATE = { analytics: false, marketing: false, decidedAt: null };

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return null;
  }
}

function writeStorage(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (err) {
    console.warn("[consent] opslaan mislukt (private mode / storage vol):", err?.message);
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStorage() || DEFAULT_STATE);
  const [decided, setDecided] = useState(() => !!readStorage());

  // Init analytics tags once on mount (Consent Mode v2 default: denied)
  useEffect(() => {
    initAnalytics();
    // Apply persisted consent on reload
    if (decided) updateConsent(consent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((next) => {
    const value = { ...next, decidedAt: new Date().toISOString() };
    setConsent(value);
    setDecided(true);
    writeStorage(value);
    updateConsent(value);
  }, []);

  const acceptAll = useCallback(() => persist({ analytics: true, marketing: true }), [persist]);
  const rejectAll = useCallback(() => persist({ analytics: false, marketing: false }), [persist]);
  const savePreferences = useCallback((prefs) => persist(prefs), [persist]);

  const revoke = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (err) { console.warn("[consent] verwijderen mislukt:", err?.message); }
    setConsent(DEFAULT_STATE);
    setDecided(false);
    updateConsent(DEFAULT_STATE);
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, decided, acceptAll, rejectAll, savePreferences, revoke }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside ConsentProvider");
  return ctx;
}
